// apps/web/src/pages/LoginPage.tsx
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import LoadingSpinner from '../components/LoadingSpinner';

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/directory', { replace: true });
      return;
    }

    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (code && state) {
      handleCallback(code, state);
    }
  }, [searchParams, navigate, user, authLoading]);

  const initiateLineLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const { url } = await api.auth.getLineUrl();
      window.location.href = url;
    } catch {
      setError('無法取得登入連結，請重新整理頁面');
      setLoading(false);
    }
  };

  const handleCallback = async (code: string, state: string) => {
    setLoading(true);
    setError(null);
    try {
      await login(code, state);
      navigate('/directory', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '登入失敗，請重試');
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 bg-mesh-green" />
      <div className="absolute inset-0 bg-[url('/cover-default.jpg')] bg-cover bg-center opacity-[0.12]" />
      <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-white/70 dark:from-gray-950/60 dark:to-gray-950/90" />

      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-slide-up">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-5 inline-flex rounded-full bg-white p-1.5 shadow-float ring-1 ring-black/5">
              <img
                src="/logo.jpg"
                alt="和春技術學院校友會"
                className="h-20 w-20 rounded-full object-contain sm:h-24 sm:w-24"
                width={96}
                height={96}
              />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              和春技術學院校友會
            </h1>
            <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
              校友名片分享平台 · FITA
            </p>
          </div>

          <div className="card-glass p-6 sm:p-8 shadow-float">
            {error && (
              <div
                className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
                role="alert"
              >
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={initiateLineLogin}
              disabled={loading}
              className="flex w-full items-center justify-center gap-3 rounded-2xl bg-[#06C755] px-4 py-3.5 text-base font-semibold text-white shadow-soft transition hover:bg-[#05ae4e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#06C755] focus-visible:ring-offset-2 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <LoadingSpinner className="w-5 h-5" />
                  <span>導向 LINE 登入中…</span>
                </>
              ) : (
                <>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                  </svg>
                  <span>使用 LINE 登入</span>
                </>
              )}
            </button>

            <p className="mt-4 text-center text-xs leading-relaxed text-gray-500 dark:text-gray-400">
              登入即可上傳名片、管理個人資料。
              <br />
              瀏覽公開名冊無需登入。
            </p>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3 text-center">
            <FeatureIcon label="名片管理" hint="上傳分享" />
            <FeatureIcon label="校友搜尋" hint="人脈連結" />
            <FeatureIcon label="公開名冊" hint="免登入瀏覽" />
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/directory"
              className="text-sm font-semibold text-alumni-700 underline-offset-4 hover:underline dark:text-alumni-300"
            >
              ← 先逛逛校友名冊
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureIcon({ label, hint }: { label: string; hint: string }) {
  return (
    <div className="rounded-2xl border border-white/70 bg-white/70 p-3 shadow-soft backdrop-blur-md dark:border-gray-800 dark:bg-gray-900/70">
      <p className="text-sm font-bold text-gray-900 dark:text-white">{label}</p>
      <p className="mt-0.5 text-2xs text-gray-500 dark:text-gray-400">{hint}</p>
    </div>
  );
}
